import type { WastewaterSummary } from '../wastewaterDataService';
import type { SectorYearValue } from './types';
import { CANONICAL_GHG_CONTRACT } from './contract';
import {
  CANONICAL_WASTEWATER_METHOD_VERSION,
  isModeledWastewaterEstimationMethod,
  resolveWastewaterInventoryYears,
  resolvedWastewaterChartValues,
  type ResolvedWastewaterYear,
} from './wastewaterInventoryResolver';
import type { WastewaterAnnualActivityDrivers } from './wastewaterAnnualActivityDrivers';

export function mapResolvedWastewaterToSectorValues(
  resolved: ResolvedWastewaterYear[]
): SectorYearValue[] {
  return resolved.map((r) => ({
    year: r.inventoryYear,
    sectorId: 'wastewater',
    displayCategory: 'Wastewater',
    emissionsMt: r.emissionsMt,
    status: r.status,
    methodVersion: r.methodVersion,
    source: r.efVersion,
    sourceDataYear: r.sourceDataYear,
    estimationMethod: r.estimationMethod,
    notes: r.notes ?? undefined,
  }));
}

export function resolveWastewaterSectorForYears(
  summary: WastewaterSummary,
  years: number[],
  activityDrivers?: WastewaterAnnualActivityDrivers | null
): { resolved: ResolvedWastewaterYear[]; sectorValues: SectorYearValue[] } {
  const resolved = resolveWastewaterInventoryYears(summary, years, activityDrivers);
  return {
    resolved,
    sectorValues: mapResolvedWastewaterToSectorValues(resolved),
  };
}

/** Chart label - modeled years identify the verified source year on the series. */
export function wastewaterChartSeriesLabel(resolved: ResolvedWastewaterYear[]): string {
  const modeled = resolved.find(
    (row) =>
      isModeledWastewaterEstimationMethod(row.estimationMethod) &&
      row.sourceDataYear != null &&
      row.sourceDataYear !== row.inventoryYear
  );
  if (modeled?.sourceDataYear != null) {
    return `Wastewater (modeled from ${modeled.sourceDataYear})`;
  }
  return 'Wastewater';
}

/** Chart overlay - uses resolver hierarchy; null for unavailable years. */
export function wastewaterChartSeries(
  summary: WastewaterSummary,
  years: number[],
  activityDrivers?: WastewaterAnnualActivityDrivers | null
): { label: string; data: (number | null)[] } | null {
  if (!summary.hasData && summary.rows.length === 0) return null;
  const resolved = resolveWastewaterInventoryYears(summary, years, activityDrivers);
  const data = resolvedWastewaterChartValues(resolved);
  if (data.every((v) => v == null)) return null;
  return { label: wastewaterChartSeriesLabel(resolved), data };
}

export { CANONICAL_WASTEWATER_METHOD_VERSION, CANONICAL_GHG_CONTRACT };
